import React, {
  useCallback,
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
} from 'react';
import { useSimulation } from '../../context/SimulationContext';

interface LineEntry {
  id: string;
  type: 'output' | 'prompt';
  text: string;
}

const OUT_BEFORE = [
  'Windows IP Configuration',
  '',
  'Wireless LAN adapter Wi-Fi:',
  '   Connection-specific DNS Suffix  :',
  '   IPv4 Address. . . . . . . . . . : 169.254.47.23',
  '   Subnet Mask . . . . . . . . . . : 255.255.0.0',
  '   Default Gateway . . . . . . . . :',
].join('\n');

const OUT_AFTER = [
  'Windows IP Configuration',
  '',
  'Wireless LAN adapter Wi-Fi:',
  '   Connection-specific DNS Suffix  : office.local',
  '   IPv4 Address. . . . . . . . . . : 192.168.1.45',
  '   Subnet Mask . . . . . . . . . . : 255.255.255.0',
  '   Default Gateway . . . . . . . . : 192.168.1.1',
  '   DNS Servers . . . . . . . . . . : 8.8.8.8',
].join('\n');

const RELEASE_OUT = [
  'Windows IP Configuration',
  '',
  'Releasing IP address for Wi-Fi...',
  '   IP address successfully released.',
].join('\n');

const RENEW_OUT = [
  'Windows IP Configuration',
  '',
  'Renewing IP address for Wi-Fi...',
  '',
  'Wireless LAN adapter Wi-Fi:',
  '   Connection-specific DNS Suffix  : office.local',
  '   IPv4 Address. . . . . . . . . . : 192.168.1.45',
  '   Subnet Mask . . . . . . . . . . : 255.255.255.0',
  '   Default Gateway . . . . . . . . : 192.168.1.1',
  '   DNS Servers . . . . . . . . . . : 8.8.8.8',
].join('\n');

const PING_BEFORE = [
  'Pinging google.com with 32 bytes of data:',
  'Request timed out.',
  'Request timed out.',
  'Request timed out.',
  'Request timed out.',
  '',
  'Ping statistics for google.com:',
  '    Packets: Sent = 4, Received = 0, Lost = 4 (100% loss)',
].join('\n');

const PING_AFTER = [
  'Pinging google.com [142.250.187.78] with 32 bytes of data:',
  'Reply from 142.250.187.78: bytes=32 time=14ms TTL=117',
  'Reply from 142.250.187.78: bytes=32 time=12ms TTL=117',
  'Reply from 142.250.187.78: bytes=32 time=13ms TTL=117',
  'Reply from 142.250.187.78: bytes=32 time=11ms TTL=117',
  '',
  'Ping statistics for google.com:',
  '    Packets: Sent = 4, Received = 4, Lost = 0 (0% loss)',
  '',
  'Approximate round trip times in milli-seconds:',
  '    Minimum = 11ms, Maximum = 14ms, Average = 12ms',
].join('\n');

const HOSTNAME_OUT = 'K-04LAPC';

const POWERCFG_BATTERYREPORT_OUT = [
  'Battery life report saved to file path',
  'C:\\Windows\\System32\\battery-report.html',
  '',
  'BATTERY 1',
  'Name:               DELL BVBEL5',
  'Manufacturer:       SMP',
  'Serial Number:      2941',
  'Chemistry:          LiI',
  'Design Capacity:    48,000 mWh',
  'Full Charge Capacity: 31,200 mWh',
  'Cycle Count:        612',
  '',
  'Design Capacity:      48,000 mWh',
  'Full Charge Capacity: 31,200 mWh',
  'Battery Health:       65%',
  '',
  'Estimated battery life at full charge: 2h 14min',
  'WARNING: Battery health is significantly degraded.',
  'Recommended action: Replace battery.',
].join('\n');

const POWERCFG_ENERGY_OUT = [
  'Enabling tracing for 60 seconds...',
  'Elapsed: 60 seconds.',
  'Analysis complete. 6 warnings found.',
  '',
  'Energy report saved to file path',
  'C:\\Windows\\System32\\energy-report.html',
  '',
  'WARNING: Battery capacity is lower than expected.',
  'Full charge capacity 31,200 mWh is significantly',
  'below design capacity of 48,000 mWh (65%).',
].join('\n');

const WMIC_BATTERY_OUT = ['EstimatedChargeRemaining', '47'].join('\n');

function uid(): string {
  return `${Date.now()}-${Math.random().toString(36).slice(2, 9)}`;
}

export function CommandPrompt({
  windowId: _windowId,
}: {
  windowId: string;
}): React.ReactElement {
  const {
    scenario,
    state,
    logAction,
    markIpReleased,
    markRenewSuccess,
    markBatteryReportGenerated,
  } = useSimulation();
  const { isFixed } = state;
  const isBatteryScenario = scenario.id === 'battery';
  const openedLogged = useRef(false);
  const [lines, setLines] = useState<LineEntry[]>(() => [
    {
      id: uid(),
      type: 'output',
      text:
        'Microsoft Windows [Version 11.0.22631.3155]\n(c) Microsoft Corporation. All rights reserved.\n\n',
    },
    { id: uid(), type: 'prompt', text: '' },
  ]);
  const [input, setInput] = useState('');
  const scrollRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLTextAreaElement>(null);

  useEffect(() => {
    if (openedLogged.current) return;
    openedLogged.current = true;
    logAction('opened_command_prompt');
  }, [logAction]);

  useEffect(() => {
    inputRef.current?.focus();
  }, [lines]);

  useLayoutEffect(() => {
    const ta = inputRef.current;
    if (!ta) return;
    ta.style.height = 'auto';
    ta.style.height = `${ta.scrollHeight}px`;
  }, [input, lines]);

  useEffect(() => {
    scrollRef.current?.scrollTo({
      top: scrollRef.current.scrollHeight,
      behavior: 'smooth',
    });
  }, [lines, input]);

  const appendSession = useCallback((cmdDisplay: string, outputBody: string) => {
    setLines((prev) => {
      const base =
        prev.length && prev[prev.length - 1]?.type === 'prompt'
          ? prev.slice(0, -1)
          : prev;
      return [
        ...base,
        { id: uid(), type: 'output', text: `${cmdDisplay}\n` },
        { id: uid(), type: 'output', text: `${outputBody}\n` },
        { id: uid(), type: 'prompt', text: '' },
      ];
    });
  }, []);

  const handleCommand = useCallback(
    (raw: string) => {
      const cmd = raw.trim();
      const lower = cmd.replace(/\s+/g, ' ').trim().toLowerCase();
      const cmdLine = `C:\\Users\\User>${cmd}`;

      if (lower === 'ipconfig') {
        logAction('ran_ipconfig');
        appendSession(cmdLine, isFixed ? OUT_AFTER : OUT_BEFORE);
        return;
      }

      if (lower === 'ipconfig /release' || lower === 'ipconfig/release') {
        logAction('ran_ipconfig_release');
        markIpReleased();
        appendSession(cmdLine, RELEASE_OUT);
        return;
      }

      if (lower === 'ipconfig /renew' || lower === 'ipconfig/renew') {
        logAction('ran_ipconfig_renew');
        const wasBroken = !isFixed;
        appendSession(cmdLine, RENEW_OUT);
        if (wasBroken) {
          markRenewSuccess();
          logAction('ip_renewed_successfully');
          logAction('scenario_resolved');
        }
        return;
      }

      if (lower === 'ping google.com') {
        if (isFixed) {
          logAction('ran_ping_after_fix');
          appendSession(cmdLine, PING_AFTER);
        } else {
          logAction('ran_ping_before_fix');
          appendSession(cmdLine, PING_BEFORE);
        }
        return;
      }

      if (lower === 'hostname') {
        logAction('ran_hostname');
        appendSession(cmdLine, HOSTNAME_OUT);
        return;
      }

      if (isBatteryScenario) {
        if (
          lower === 'powercfg /batteryreport' ||
          lower === 'powercfg/batteryreport'
        ) {
          logAction('ran_powercfg_batteryreport');
          markBatteryReportGenerated();
          logAction('powercfg_report_generated');
          appendSession(cmdLine, POWERCFG_BATTERYREPORT_OUT);
          return;
        }

        if (lower === 'powercfg /energy' || lower === 'powercfg/energy') {
          logAction('ran_powercfg_energy');
          appendSession(cmdLine, POWERCFG_ENERGY_OUT);
          return;
        }

        if (lower === 'wmic path win32_battery get estimatedchargeremaining') {
          logAction('ran_wmic_battery');
          appendSession(cmdLine, WMIC_BATTERY_OUT);
          return;
        }
      }

      const safe = cmd.replace(/'/g, "''");
      appendSession(
        cmdLine,
        `'${safe}' is not recognized as an internal or external command,\noperable program or batch file.`
      );
    },
    [
      appendSession,
      isBatteryScenario,
      isFixed,
      logAction,
      markBatteryReportGenerated,
      markIpReleased,
      markRenewSuccess,
    ]
  );

  const onKeyDown = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      const v = input;
      setInput('');
      handleCommand(v);
    }
  };

  return (
    <div
      className="flex flex-1 min-h-0 flex-col bg-[#0C0C0C] font-mono text-sm font-normal text-white leading-relaxed selection:bg-white/25 cursor-text"
      onMouseDown={() => inputRef.current?.focus()}
    >
      <div
        ref={scrollRef}
        className="flex-1 min-h-0 overflow-y-auto overflow-x-hidden p-3"
      >
        {lines.map((line) => {
          if (line.type === 'output') {
            return (
              <span
                key={line.id}
                className="block whitespace-pre-wrap break-words text-white"
              >
                {line.text}
              </span>
            );
          }
          return (
            <div
              key={line.id}
              className="flex w-full min-w-0 flex-row flex-nowrap items-baseline"
            >
              <span className="shrink-0 whitespace-pre text-white">
                C:\Users\User&gt;
              </span>
              <textarea
                ref={inputRef}
                rows={1}
                wrap="soft"
                autoComplete="off"
                spellCheck={false}
                aria-label="Command line"
                className="min-h-[1.25em] min-w-0 flex-1 resize-none border-0 bg-transparent p-0 m-0 shadow-none outline-none ring-0 focus:ring-0 focus:outline-none focus-visible:outline-none text-inherit font-inherit font-normal leading-inherit text-white caret-white placeholder:text-transparent whitespace-pre-wrap break-words overflow-hidden"
                value={input}
                onChange={(ev) => setInput(ev.target.value)}
                onKeyDown={onKeyDown}
              />
            </div>
          );
        })}
      </div>
    </div>
  );
}
